import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { endpoints, urlAdminOrgSubscription } from "../config/api";

const DEV_BRANCH_MSG =
  "A different branch is deployed in the backend for testing — please reach out to the backend team.";

function apiError(error, fallback) {
  if (error?.response?.status === 404) return DEV_BRANCH_MSG;
  return getErrorMessage(error, fallback);
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function toDateInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function statusMeta(status) {
  switch (status) {
    case "active":
      return { label: "Active", bg: "#d1fae5", color: "#047857", border: "#10b981" };
    case "grace_period":
      return { label: "Grace period", bg: "#fef3c7", color: "#b45309", border: "#f59e0b" };
    case "expired":
    default:
      return { label: "Expired", bg: "#fee2e2", color: "#b91c1c", border: "#ef4444" };
  }
}

const emptySubscriptionForm = {
  next_payment_date: "",
  grace_period_days: "15",
  is_active: true,
  number_of_users: "",
  devices_per_user: "2",
};

const emptyInvoiceForm = {
  months_paid: "1",
  users_paid_for: "",
  invoice_number: "",
  paid_at: "",
  note: "",
};

const UserInvoices = ({ onClose }) => {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingSub, setLoadingSub] = useState(false);
  const [summary, setSummary] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [subscriptionForm, setSubscriptionForm] = useState(emptySubscriptionForm);
  const [invoiceForm, setInvoiceForm] = useState(emptyInvoiceForm);
  const [savingSub, setSavingSub] = useState(false);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const clearMessages = () => {
    setSuccessMessage("");
    setErrorMessage("");
  };

  useEffect(() => {
    const fetchOrgs = async () => {
      setLoadingOrgs(true);
      try {
        const response = await axios.get(endpoints.orgs, {
          headers: {
            ...getAuthHeaders(),
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        });
        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        setOrgs(list);
      } catch (error) {
        console.error("Error fetching orgs", error);
        setErrorMessage(apiError(error, "Failed to fetch organizations."));
      } finally {
        setLoadingOrgs(false);
      }
    };

    fetchOrgs();
  }, []);

  const applySubscriptionData = useCallback((data) => {
    const nextSummary = data?.summary ?? null;
    const nextSub = data?.subscription ?? null;
    const nextInvoices = Array.isArray(data?.invoices)
      ? data.invoices
      : Array.isArray(nextSub?.invoices)
      ? nextSub.invoices
      : [];

    setSummary(nextSummary);
    setSubscription(nextSub);
    setInvoices(nextInvoices);

    setSubscriptionForm({
      next_payment_date: toDateInputValue(
        nextSub?.next_payment_date ?? nextSummary?.next_payment_date
      ),
      grace_period_days: String(
        nextSub?.grace_period_days ?? nextSummary?.grace_period_days ?? 15
      ),
      is_active: nextSub?.is_active !== false,
      number_of_users:
        nextSub?.number_of_users != null
          ? String(nextSub.number_of_users)
          : nextSummary?.number_of_users != null
          ? String(nextSummary.number_of_users)
          : "",
      devices_per_user: String(
        nextSub?.devices_per_user ?? nextSummary?.devices_per_user ?? 2
      ),
    });
  }, []);

  const loadSubscription = useCallback(
    async (organizationId) => {
      if (!organizationId) {
        setSummary(null);
        setSubscription(null);
        setInvoices([]);
        setSubscriptionForm(emptySubscriptionForm);
        return;
      }

      setLoadingSub(true);
      clearMessages();
      try {
        const response = await axios.get(urlAdminOrgSubscription(organizationId), {
          headers: {
            ...getAuthHeaders(),
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        });
        const data = response.data?.data ?? response.data;
        applySubscriptionData(data);
      } catch (error) {
        console.error("Error fetching subscription", error);
        setErrorMessage(apiError(error, "Failed to load subscription."));
        setSummary(null);
        setSubscription(null);
        setInvoices([]);
        setSubscriptionForm(emptySubscriptionForm);
      } finally {
        setLoadingSub(false);
      }
    },
    [applySubscriptionData]
  );

  useEffect(() => {
    if (selectedOrgId) {
      loadSubscription(selectedOrgId);
    } else {
      setSummary(null);
      setSubscription(null);
      setInvoices([]);
      setSubscriptionForm(emptySubscriptionForm);
      setInvoiceForm(emptyInvoiceForm);
    }
  }, [selectedOrgId, loadSubscription]);

  const updateSubField = (field, value) => {
    setSubscriptionForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateInvoiceField = (field, value) => {
    setInvoiceForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSubscription = async (event) => {
    event.preventDefault();
    if (!selectedOrgId) {
      setErrorMessage("Please select an organization.");
      return;
    }

    const graceDays = Number(subscriptionForm.grace_period_days);
    if (!Number.isInteger(graceDays) || graceDays < 0) {
      setErrorMessage("Grace period days must be a non-negative integer.");
      return;
    }

    const devices = Number(subscriptionForm.devices_per_user);
    if (!Number.isInteger(devices) || devices < 1) {
      setErrorMessage("Devices per user must be a positive integer.");
      return;
    }

    let numberOfUsers;
    if (subscriptionForm.number_of_users !== "") {
      numberOfUsers = Number(subscriptionForm.number_of_users);
      if (!Number.isInteger(numberOfUsers) || numberOfUsers < 0) {
        setErrorMessage("Number of users must be a non-negative integer.");
        return;
      }
    }

    setSavingSub(true);
    clearMessages();
    try {
      const body = {
        organizationId: Number(selectedOrgId),
        grace_period_days: graceDays,
        is_active: Boolean(subscriptionForm.is_active),
        devices_per_user: devices,
      };
      if (subscriptionForm.next_payment_date) {
        body.next_payment_date = subscriptionForm.next_payment_date;
      }
      if (numberOfUsers !== undefined) {
        body.number_of_users = numberOfUsers;
      }

      await axios.patch(endpoints.adminSubscriptions, body, {
        headers: {
          ...getAuthHeaders(),
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      });
      setSuccessMessage("Subscription saved.");
      await loadSubscription(selectedOrgId);
    } catch (error) {
      console.error("Error saving subscription", error);
      setErrorMessage(apiError(error, "Failed to save subscription."));
    } finally {
      setSavingSub(false);
    }
  };

  const handleCreateInvoice = async (event) => {
    event.preventDefault();
    if (!selectedOrgId) {
      setErrorMessage("Please select an organization.");
      return;
    }

    const monthsPaid = Number(invoiceForm.months_paid);
    if (!Number.isInteger(monthsPaid) || monthsPaid < 1) {
      setErrorMessage("Months paid must be a positive integer.");
      return;
    }

    let usersPaidFor;
    if (invoiceForm.users_paid_for !== "") {
      usersPaidFor = Number(invoiceForm.users_paid_for);
      if (!Number.isInteger(usersPaidFor) || usersPaidFor < 0) {
        setErrorMessage("Users paid for must be a non-negative integer.");
        return;
      }
    }

    setSavingInvoice(true);
    clearMessages();
    try {
      const body = {
        organizationId: Number(selectedOrgId),
        months_paid: monthsPaid,
      };
      if (usersPaidFor !== undefined) body.users_paid_for = usersPaidFor;
      if (invoiceForm.invoice_number.trim()) {
        body.invoice_number = invoiceForm.invoice_number.trim();
      }
      if (invoiceForm.paid_at) body.paid_at = invoiceForm.paid_at;
      if (invoiceForm.note.trim()) body.note = invoiceForm.note.trim();

      await axios.post(endpoints.adminInvoices, body, {
        headers: {
          ...getAuthHeaders(),
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      });
      setSuccessMessage("Invoice recorded. Next payment date updated.");
      setInvoiceForm(emptyInvoiceForm);
      await loadSubscription(selectedOrgId);
    } catch (error) {
      console.error("Error creating invoice", error);
      setErrorMessage(apiError(error, "Failed to record invoice."));
    } finally {
      setSavingInvoice(false);
    }
  };

  const status = statusMeta(summary?.status);
  const orgId = (org) => org.id ?? org.organizationId ?? org.organization_id;
  const orgName = (org) =>
    org.name ?? org.orgName ?? org.organization_name ?? `Org ${orgId(org)}`;

  return (
    <div>
      {onClose && (
        <button type="button" style={closeButtonStyle} onClick={onClose}>
          Close
        </button>
      )}

      <h2 style={headingStyle}>User Invoices</h2>
      <p style={subtitleStyle}>
        View computed subscription status and manage invoices for an organization.
      </p>

      {successMessage && <div style={successMessageStyle}>{successMessage}</div>}
      {errorMessage && <div style={errorMessageStyle}>{errorMessage}</div>}

      <div style={formGroupStyle}>
        <label htmlFor="invoice-org-select" style={labelStyle}>
          Select Organization
        </label>
        <select
          id="invoice-org-select"
          value={selectedOrgId}
          onChange={(e) => setSelectedOrgId(e.target.value)}
          disabled={loadingOrgs}
          style={selectStyle}
        >
          <option value="">
            {loadingOrgs ? "Loading organizations..." : "Select an organization"}
          </option>
          {orgs.map((org) => {
            const id = orgId(org);
            return (
              <option key={id} value={id}>
                {orgName(org)}
              </option>
            );
          })}
        </select>
      </div>

      {!selectedOrgId ? (
        <p style={hintStyle}>Choose an organization to view subscription and invoices.</p>
      ) : loadingSub ? (
        <p style={hintStyle}>Loading subscription…</p>
      ) : (
        <>
          <section style={sectionCard}>
            <h3 style={sectionTitleStyle}>Subscription status</h3>
            <div style={statusBadgeStyle(status)}>{status.label}</div>
            <div style={summaryGridStyle}>
              <SummaryItem label="Next payment date" value={formatDate(summary?.next_payment_date)} />
              <SummaryItem label="Grace period (days)" value={summary?.grace_period_days ?? "—"} />
              <SummaryItem label="Grace ends at" value={formatDate(summary?.grace_ends_at)} />
              <SummaryItem
                label="Number of users"
                value={
                  summary?.number_of_users != null
                    ? summary.number_of_users
                    : subscription?.number_of_users != null
                    ? subscription.number_of_users
                    : "—"
                }
              />
              <SummaryItem
                label="Devices per user"
                value={
                  summary?.devices_per_user != null
                    ? summary.devices_per_user
                    : subscription?.devices_per_user != null
                    ? subscription.devices_per_user
                    : "—"
                }
              />
              <SummaryItem
                label="Subscription active flag"
                value={subscription?.is_active === false ? "No" : "Yes"}
              />
            </div>
          </section>

          <div style={formsRowStyle}>
            <section style={{ ...sectionCard, flex: 1, minWidth: 280 }}>
              <h3 style={sectionTitleStyle}>Update subscription</h3>
              <form onSubmit={handleSaveSubscription}>
                <div style={formGroupStyle}>
                  <label htmlFor="next-payment-date" style={labelStyle}>
                    Next payment date
                  </label>
                  <input
                    id="next-payment-date"
                    type="date"
                    value={subscriptionForm.next_payment_date}
                    onChange={(e) => updateSubField("next_payment_date", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div style={formGroupStyle}>
                  <label htmlFor="grace-period-days" style={labelStyle}>
                    Grace period (days)
                  </label>
                  <input
                    id="grace-period-days"
                    type="number"
                    min="0"
                    step="1"
                    value={subscriptionForm.grace_period_days}
                    onChange={(e) => updateSubField("grace_period_days", e.target.value)}
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label htmlFor="number-of-users" style={labelStyle}>
                    Number of users
                  </label>
                  <input
                    id="number-of-users"
                    type="number"
                    min="0"
                    step="1"
                    value={subscriptionForm.number_of_users}
                    onChange={(e) => updateSubField("number_of_users", e.target.value)}
                    style={inputStyle}
                    placeholder="Optional override"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label htmlFor="devices-per-user" style={labelStyle}>
                    Devices per user
                  </label>
                  <input
                    id="devices-per-user"
                    type="number"
                    min="1"
                    step="1"
                    value={subscriptionForm.devices_per_user}
                    onChange={(e) => updateSubField("devices_per_user", e.target.value)}
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={{ ...formGroupStyle, display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    id="is-active"
                    type="checkbox"
                    checked={subscriptionForm.is_active}
                    onChange={(e) => updateSubField("is_active", e.target.checked)}
                  />
                  <label htmlFor="is-active" style={{ ...labelStyle, marginBottom: 0 }}>
                    Subscription active
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={savingSub}
                  style={{
                    ...buttonStyle,
                    opacity: savingSub ? 0.6 : 1,
                    cursor: savingSub ? "not-allowed" : "pointer",
                  }}
                >
                  {savingSub ? "Saving…" : "Save subscription"}
                </button>
              </form>
            </section>

            <section style={{ ...sectionCard, flex: 1, minWidth: 280 }}>
              <h3 style={sectionTitleStyle}>Record invoice / payment</h3>
              <p style={{ ...hintStyle, marginTop: 0 }}>
                Advances next payment date by the months paid (stacks onto remaining paid time).
              </p>
              <form onSubmit={handleCreateInvoice}>
                <div style={formGroupStyle}>
                  <label htmlFor="months-paid" style={labelStyle}>
                    Months paid
                  </label>
                  <input
                    id="months-paid"
                    type="number"
                    min="1"
                    step="1"
                    value={invoiceForm.months_paid}
                    onChange={(e) => updateInvoiceField("months_paid", e.target.value)}
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label htmlFor="users-paid-for" style={labelStyle}>
                    Users paid for
                  </label>
                  <input
                    id="users-paid-for"
                    type="number"
                    min="0"
                    step="1"
                    value={invoiceForm.users_paid_for}
                    onChange={(e) => updateInvoiceField("users_paid_for", e.target.value)}
                    style={inputStyle}
                    placeholder="Updates number of users seats"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label htmlFor="invoice-number" style={labelStyle}>
                    Invoice number
                  </label>
                  <input
                    id="invoice-number"
                    type="text"
                    value={invoiceForm.invoice_number}
                    onChange={(e) => updateInvoiceField("invoice_number", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div style={formGroupStyle}>
                  <label htmlFor="paid-at" style={labelStyle}>
                    Paid at
                  </label>
                  <input
                    id="paid-at"
                    type="date"
                    value={invoiceForm.paid_at}
                    onChange={(e) => updateInvoiceField("paid_at", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div style={formGroupStyle}>
                  <label htmlFor="invoice-note" style={labelStyle}>
                    Note
                  </label>
                  <textarea
                    id="invoice-note"
                    value={invoiceForm.note}
                    onChange={(e) => updateInvoiceField("note", e.target.value)}
                    style={{ ...inputStyle, minHeight: 72, resize: "vertical" }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={savingInvoice}
                  style={{
                    ...buttonStyle,
                    opacity: savingInvoice ? 0.6 : 1,
                    cursor: savingInvoice ? "not-allowed" : "pointer",
                  }}
                >
                  {savingInvoice ? "Recording…" : "Record invoice"}
                </button>
              </form>
            </section>
          </div>

          <section style={{ ...sectionCard, maxWidth: "100%", marginTop: 20 }}>
            <h3 style={sectionTitleStyle}>Invoice history</h3>
            {invoices.length === 0 ? (
              <p style={hintStyle}>No invoices yet for this organization.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Invoice #</th>
                      <th style={thStyle}>Months</th>
                      <th style={thStyle}>Users</th>
                      <th style={thStyle}>Period start</th>
                      <th style={thStyle}>Period end</th>
                      <th style={thStyle}>Paid at</th>
                      <th style={thStyle}>Added by</th>
                      <th style={thStyle}>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id ?? `${inv.invoice_number}-${inv.paid_at}`}>
                        <td style={tdStyle}>{inv.invoice_number || "—"}</td>
                        <td style={tdStyle}>{inv.months_paid ?? "—"}</td>
                        <td style={tdStyle}>
                          {inv.users_paid_for != null ? inv.users_paid_for : "—"}
                        </td>
                        <td style={tdStyle}>{formatDate(inv.period_start)}</td>
                        <td style={tdStyle}>{formatDate(inv.period_end)}</td>
                        <td style={tdStyle}>{formatDate(inv.paid_at)}</td>
                        <td style={tdStyle}>{inv.added_by || "—"}</td>
                        <td style={tdStyle}>{inv.note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

function SummaryItem({ label, value }) {
  return (
    <div>
      <div style={summaryLabelStyle}>{label}</div>
      <div style={summaryValueStyle}>{value}</div>
    </div>
  );
}

const headingStyle = {
  color: "#3730a3",
  fontSize: "28px",
  fontWeight: "bold",
  marginBottom: "8px",
  lineHeight: "1.2",
};

const subtitleStyle = {
  color: "#4b5563",
  fontSize: "14px",
  marginBottom: "24px",
  marginTop: 0,
};

const closeButtonStyle = {
  height: "40px",
  padding: "0 16px",
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "600",
  marginBottom: "20px",
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  fontSize: "14px",
  backgroundColor: "#ffffff",
  border: "1px solid #3730a3",
  borderRadius: "6px",
  boxSizing: "border-box",
  color: "#1f2937",
  outline: "none",
};

const selectStyle = {
  ...inputStyle,
  cursor: "pointer",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontSize: "14px",
  fontWeight: "600",
  color: "#3730a3",
};

const buttonStyle = {
  padding: "12px 24px",
  fontSize: "14px",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "600",
};

const formGroupStyle = {
  marginBottom: "20px",
};

const successMessageStyle = {
  color: "#047857",
  marginBottom: "16px",
  padding: "12px",
  backgroundColor: "#d1fae5",
  borderRadius: "6px",
  border: "1px solid #10b981",
};

const errorMessageStyle = {
  color: "#b91c1c",
  marginBottom: "16px",
  padding: "12px",
  backgroundColor: "#fee2e2",
  borderRadius: "6px",
  border: "1px solid #ef4444",
};

const sectionCard = {
  width: "100%",
  maxWidth: "100%",
  padding: "24px",
  borderRadius: "8px",
  border: "1px solid #c7d2fe",
  backgroundColor: "#fafbff",
  boxSizing: "border-box",
  marginBottom: "20px",
};

const sectionTitleStyle = {
  color: "#3730a3",
  fontSize: "18px",
  fontWeight: "700",
  marginTop: 0,
  marginBottom: "16px",
};

const hintStyle = {
  color: "#6b7280",
  fontSize: "14px",
};

const formsRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "20px",
  alignItems: "flex-start",
};

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
  gap: "16px",
  marginTop: "16px",
};

const summaryLabelStyle = {
  fontSize: "12px",
  fontWeight: "600",
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  marginBottom: "4px",
};

const summaryValueStyle = {
  fontSize: "16px",
  fontWeight: "600",
  color: "#1f2937",
};

const statusBadgeStyle = (meta) => ({
  display: "inline-block",
  padding: "6px 12px",
  borderRadius: "6px",
  backgroundColor: meta.bg,
  color: meta.color,
  border: `1px solid ${meta.border}`,
  fontSize: "13px",
  fontWeight: "700",
});

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "14px",
};

const thStyle = {
  textAlign: "left",
  padding: "12px 14px",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  fontWeight: "600",
  border: "1px solid #312e81",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "12px 14px",
  border: "1px solid #e5e7eb",
  color: "#1f2937",
  verticalAlign: "top",
};

export default UserInvoices;
